/**
 * 入札系の数量 (MW) を小数 1 桁 (四捨五入) の 3 桁区切り文字列にする。
 *
 * 数量値の表示精度は小数 1 桁が基本 (スポット AC-BSP-053 / 時間前 AC-BIN-043)。
 * JEPX の最小取引単位が 0.1 MW なので整数に丸めると 1 MW 入札の 0.5 MW 部分約定が
 * 全量約定と同じ 1 MW に、0.4 MW の約定が 0 MW に見え、調達過不足を誤認する
 * (issue #6492)。
 *
 * 丸めは四捨五入 (docs/specs/README.md §数値表示共通ルール)。`Math.round` は
 * half-up (+∞ 方向) で負値が四捨五入にならない (-0.25 → -0.2) ため、絶対値で
 * 丸めてから符号を戻す (`formatKwh` と同じ half-away-from-zero)。調達必要量は
 * 売り余剰コマで負値を取り (ADR `bidding-position-need-bilateral-baseline.md`)、
 * カバー量も逆方向入札で負値になりうる。
 *
 * NOTE: 単位ラベル (MW) は呼び出し側が持つ。本関数は丸め方だけを担う。
 *
 * why not チャート系列のプロット値にも使う: 戻り値は表示テキスト専用であり、
 * Recharts の dataKey (系列描画値) には使わない — 描画は小数を保持する
 * (docs/specs/conventions.md「チャートの描画値と軸目盛りの丸め方は分けて扱う」)。
 */
export function formatMw(value: number): string {
  const rounded = (Math.sign(value) * Math.round(Math.abs(value) * 10)) / 10
  // -0 を "-0.0" と出さないよう 0 に正規化する (Math.sign(-0.04) * 0 = -0)。
  return (rounded === 0 ? 0 : rounded).toLocaleString('ja-JP', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })
}

/**
 * 入札系の MW 集計値 (複数コマ・複数注文の単純合算) を小数 2 桁 (四捨五入) の
 * 3 桁区切り文字列にする。
 *
 * 集計値・サマリーは小数 2 桁 (docs/specs/conventions.md「数値表示」、
 * 時間前 AC-BIN-043)。個別行 (コマ別の入札量・約定量) は入力精度どおり `formatMw`
 * を使う。丸めは `formatMw` と同じ half-away-from-zero。
 */
export function formatMwSum(value: number): string {
  const rounded = (Math.sign(value) * Math.round(Math.abs(value) * 100)) / 100
  return (rounded === 0 ? 0 : rounded).toLocaleString('ja-JP', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/**
 * 連系線空容量 (MW) を小数 3 桁まで・3 桁区切りにする。値なしは「—」。
 * 元データの精度 (小数 3 桁) まで出し、 末尾はゼロで埋めない (spec AC-BIN-030 /
 * AC-BIN-011、 PdM 判断 2026-10-10 #9591。 1.5 は 1.500 でなく 1.5。 表示桁の共通ルール
 * docs/specs/README.md「数値表示共通ルール」 とも揃う)。
 */
export function formatInterconMw(value: number | undefined): string {
  if (value === undefined) return '—'
  return value.toLocaleString('ja-JP', { maximumFractionDigits: 3 })
}
